import React, { useEffect, useState } from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import { showError, showSuccess } from '../../toast';
import { api, API_URL } from '../../api';
import { Button, Card, COLORS, Input, Label, Muted, Screen, Title } from '../../ui';
import { pickAndUploadPhotos } from '../../photo-picker';
import { t } from '../../i18n';

// Полетата, които се пренасят от договора в протокола
const PARTY_KEYS = [
  'landlordName', 'landlordEgn', 'landlordIdCard', 'landlordIdIssued', 'landlordIdIssuer', 'landlordAddress',
  'tenantName', 'tenantEgn', 'tenantIdCard', 'tenantIdIssued', 'tenantIdIssuer', 'tenantAddress',
  'tenant2Name', 'tenant2Egn', 'tenant2IdCard', 'tenant2IdIssued', 'tenant2IdIssuer', 'tenant2Address',
  'propertyLine1', 'propertyLine2',
];

export default function CreateHandoverScreen({ route, navigation }: any) {
  const { tenancyId } = route.params;
  const [type, setType] = useState<'MOVE_IN' | 'MOVE_OUT'>('MOVE_IN');
  const [photos, setPhotos] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  // Данните на страните и имота — същите като в договора. Копират се
  // АВТОМАТИЧНО при отваряне на екрана; наемодателят може да ги премахне.
  const [parties, setParties] = useState<Record<string, string> | null>(null);
  const [copying, setCopying] = useState(true);

  const copyFromContract = async () => {
    setCopying(true);
    try {
      const r = await api(`/documents/contract/data?tenancyId=${tenancyId}`);
      const picked: Record<string, string> = {};
      for (const k of PARTY_KEYS) if (r.fields?.[k]) picked[k] = r.fields[k];
      setParties(picked);
    } catch (e: any) {
      showError(e.message);
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    copyFromContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenancyId]);

  const pickPhoto = async () => {
    // избор на НЯКОЛКО снимки наведнъж — качват се паралелно
    const urls = await pickAndUploadPhotos();
    if (urls.length) setPhotos((p) => [...p, ...urls]);
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api(`/tenancies/${tenancyId}/handover`, {
        body: { type, photos, notes: notes || undefined, ...(parties ? { parties } : {}) },
      });
      showSuccess(t('Наемателят получи известие да потвърди състоянието.'));
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Приемо-предавателен протокол')}</Title>
      <Card>
        <Label>{t('Вид')}</Label>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {(
            [
              ['MOVE_IN', t('При настаняване')],
              ['MOVE_OUT', t('При напускане')],
            ] as const
          ).map(([key, label]) => (
            <Pressable
              key={key}
              onPress={() => setType(key)}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                alignItems: 'center',
                backgroundColor: type === key ? COLORS.primary : COLORS.bg,
                borderWidth: 1,
                borderColor: type === key ? COLORS.primary : COLORS.border,
              }}
            >
              <Text style={{ color: type === key ? '#fff' : COLORS.text }}>{label}</Text>
            </Pressable>
          ))}
        </View>

        <Label>{t('Данни на страните и имота')}</Label>
        {copying ? (
          <Muted>{t('Зареждам данните от договора…')}</Muted>
        ) : parties ? (
          <View style={{ backgroundColor: COLORS.bg, borderRadius: 8, padding: 12 }}>
            <Text style={{ color: COLORS.success, fontWeight: '600', marginBottom: 6 }}>
              {t('✓ Копирани от договора — влизат в протокола')}
            </Text>
            <Text style={{ color: COLORS.text, marginBottom: 4 }}>
              🏠 {[parties.propertyLine1, parties.propertyLine2].filter(Boolean).join(', ')}
            </Text>
            <Text style={{ color: COLORS.text, marginBottom: 4 }}>{t('Наемодател: {name}', { name: parties.landlordName ?? '—' })}</Text>
            <Text style={{ color: COLORS.text }}>{t('Наемател: {name}', { name: parties.tenantName ?? '—' })}</Text>
            {parties.tenant2Name ? (
              <Text style={{ color: COLORS.text, marginTop: 4 }}>{t('Втори наемател: {name}', { name: parties.tenant2Name })}</Text>
            ) : null}
            <Muted>{t('ЕГН, лична карта и адресите също влизат в протокола.')}</Muted>
            <Button title={t('Не включвай данните')} variant="secondary" onPress={() => setParties(null)} />
          </View>
        ) : (
          <>
            <Muted>
              {t('Пренася имената, ЕГН, личната карта и адресите на двете страни и описанието на имота — точно както са в договора.')}
            </Muted>
            <Button
              title={t('Копирай данните от договора')}
              variant="secondary"
              onPress={copyFromContract}
              loading={copying}
            />
          </>
        )}

        <Label>{t('Снимки на състоянието ({n})', { n: photos.length })}</Label>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {photos.map((p) => (
            <Image key={p} source={{ uri: `${API_URL}${p}` }} style={{ width: 72, height: 72, borderRadius: 8 }} />
          ))}
        </View>
        <Button title={t('+ Добави снимка')} variant="secondary" onPress={pickPhoto} />

        <Label>{t('Забележки')}</Label>
        <Input value={notes} onChangeText={setNotes} multiline placeholder={t('Без забележки')} />

        <Muted>{t('Наемателят ще потвърди в приложението, че приема имота в това състояние (с точен час на потвърждението).')}</Muted>
        <Button title={t('Изпрати за потвърждение')} onPress={submit} disabled={photos.length === 0} loading={loading} />
      </Card>
    </Screen>
  );
}
